import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Navigation, LogOut, Bike, CheckCircle2, MessageCircle, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MapView, { gmapsLink } from "@/components/MapView";
import { Logo, InstallAppButton } from "@/components/Brand";
import { errMsg, fmt, fmtTime, headerApi, PAYMENTS, STATUSES } from "@/lib/api";
import { playAlarm, unlockAudio } from "@/lib/sound";
import { PaymentPicker } from "@/pages/panel/NewOrderDialog";

const KEY = "gs_courier_token";

function Login({ onLogin }) {
  const [code, setCode] = useState("");
  const submit = async () => {
    try {
      const { data } = await headerApi().post("/courier/login", { code });
      localStorage.setItem(KEY, data.token);
      unlockAudio();
      onLogin(data.token);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen bg-[#0F1F45] text-white flex flex-col items-center justify-center p-6 gap-6" data-testid="courier-login">
      <div className="bg-white rounded-3xl p-2"><Logo className="h-20 w-20" /></div>
      <div className="text-center"><h1 className="font-display text-3xl font-extrabold">Gazi Sepet Kurye</h1><p className="text-white/60 text-sm mt-1">İşletmenizin verdiği kurye kodunu girin</p></div>
      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="KURYE KODU" className="max-w-xs h-14 text-center text-2xl tracking-[0.3em] font-mono-num bg-white text-[#0F1F45]" data-testid="courier-code-input" />
      <Button className="w-full max-w-xs h-12 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={submit} data-testid="courier-login-btn">Giriş Yap</Button>
      <InstallAppButton testId="courier-install-btn" />
    </div>
  );
}

function OrderCard({ o, api, me, onDone }) {
  const [pay, setPay] = useState(o.payment_method);
  const update = async (status) => {
    try { await api.patch(`/courier/orders/${o.id}`, { status, payment_method: pay }); toast.success(status === "teslim" ? "Teslim edildi" : "Yola çıkıldı"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const points = [{ lat: o.lat, lng: o.lng, label: o.customer_name || `#${o.no}`, color: "#FF6B0A" }];
  if (me?.lat != null) points.push({ lat: me.lat, lng: me.lng, label: "Ben", color: "#0F1F45" });
  const wa = o.customer_phone ? `https://wa.me/${o.customer_phone.replace(/\D/g, "").replace(/^0/, "90")}` : null;
  return (
    <div className="rounded-3xl bg-white border overflow-hidden" data-testid={`courier-order-${o.no}`}>
      <div className="p-4 flex items-center gap-2">
        <span className="font-display text-2xl font-extrabold">#{o.no}</span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${o.status === "yolda" ? "bg-sky-100 text-sky-800" : "bg-[#FF6B0A] text-white"}`}>{STATUSES[o.status]}</span>
        <span className="ml-auto text-xs text-slate-500">{fmtTime(o.created_at)}</span>
      </div>
      {(o.lat != null || me?.lat != null) && <MapView points={points} className="h-48 mx-4" testId={`courier-order-map-${o.no}`} />}
      <div className="p-4 space-y-2">
        <p className="font-semibold text-lg" data-testid={`courier-order-customer-${o.no}`}>{o.customer_name || "Müşteri"}</p>
        {o.customer_phone && <p className="font-mono-num text-lg" data-testid={`courier-order-phone-${o.no}`}>{o.customer_phone}</p>}
        <p className="flex items-start gap-1.5 text-sm text-slate-600"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#FF6B0A]" />{o.address || "Adres girilmemiş"}</p>
        {o.note && <p className="text-xs bg-amber-50 rounded-lg p-2">Not: {o.note}</p>}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {o.customer_phone && <a href={`tel:${o.customer_phone}`} data-testid={`courier-call-btn-${o.no}`}><Button className="w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 h-12 gap-1"><Phone className="h-4 w-4" />Ara</Button></a>}
          {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid={`courier-wa-btn-${o.no}`}><Button className="w-full rounded-2xl bg-[#25D366] hover:bg-[#1eb457] h-12 gap-1"><MessageCircle className="h-4 w-4" />Yaz</Button></a>}
          <a href={gmapsLink(o)} target="_blank" rel="noreferrer" data-testid={`courier-nav-btn-${o.no}`}><Button className="w-full rounded-2xl bg-[#0F1F45] h-12 gap-1"><Navigation className="h-4 w-4" />Yol Tarifi</Button></a>
        </div>
        <div className="border-t border-dashed pt-2 text-sm">
          {o.items.map((i, idx) => <div key={idx} className="flex justify-between"><span>{i.qty}x {i.name}</span><span>{fmt(i.price * i.qty)}</span></div>)}
          <div className="flex justify-between font-bold text-lg mt-1"><span>Tahsil edilecek</span><span data-testid={`courier-order-total-${o.no}`}>{fmt(o.total)}</span></div>
          <p className="text-xs text-slate-500">Ödeme: {PAYMENTS[o.payment_method]}</p>
        </div>
        {o.status !== "yolda" ? (
          <Button className="w-full h-12 rounded-2xl bg-sky-600 hover:bg-sky-700 gap-2" onClick={() => update("yolda")} data-testid={`courier-onway-btn-${o.no}`}><Bike className="h-5 w-5" />Yola Çıktım</Button>
        ) : (
          <div className="space-y-2">
            <PaymentPicker value={pay} onChange={setPay} prefix={`courier-pay-${o.no}`} />
            <Button className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => update("teslim")} data-testid={`courier-deliver-btn-${o.no}`}><CheckCircle2 className="h-5 w-5" />Teslim Ettim</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const api = useRef(headerApi("X-Courier-Token", token)).current;
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState({ active: [], delivered_today: [] });
  const [tab, setTab] = useState("aktif");
  const [pos, setPos] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/courier/orders");
      setOrders(data);
      if (seen.current === null) seen.current = new Set(data.active.map((o) => o.id));
      else {
        const fresh = data.active.filter((o) => !seen.current.has(o.id));
        fresh.forEach((o) => seen.current.add(o.id));
        if (fresh.length) { playAlarm(); toast.success(`Yeni teslimat: #${fresh[0].no}`); }
      }
    } catch (e) { if (e?.response?.status === 401) onLogout(); }
  }, [api, onLogout]);

  useEffect(() => {
    api.get("/courier/me").then((r) => setMe(r.data)).catch(() => onLogout());
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [api, load, onLogout]);

  useEffect(() => {
    if (!navigator.geolocation) { setGeoErr("Cihaz konum desteklemiyor"); return; }
    let last = 0;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        const c = { lat: p.coords.latitude, lng: p.coords.longitude };
        setPos(c); setGeoErr("");
        if (Date.now() - last > 15000) { last = Date.now(); api.post("/courier/location", c).catch(() => {}); }
      },
      () => setGeoErr("Konum izni verilmedi. Konumunuzu paylaşmak için izin verin."),
      { enableHighAccuracy: true, maximumAge: 10000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [api]);

  const list = tab === "aktif" ? orders.active : orders.delivered_today;
  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="courier-dashboard">
      <header className="bg-[#0F1F45] text-white px-5 pt-6 pb-5 rounded-b-[2rem]">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-2xl p-1"><Logo className="h-10 w-10" /></div>
          <div className="flex-1"><p className="font-display text-xl font-bold" data-testid="courier-name">{me?.courier?.name}</p><p className="text-xs text-white/60">{me?.business?.name}</p></div>
          <Button size="icon" variant="ghost" className="text-white" onClick={onLogout} data-testid="courier-logout-btn"><LogOut className="h-5 w-5" /></Button>
        </div>
        <p className={`mt-3 text-xs flex items-center gap-1.5 ${pos ? "text-emerald-300" : "text-amber-300"}`} data-testid="courier-location-status">
          <LocateFixed className="h-3.5 w-3.5" />{pos ? "Konum paylaşılıyor" : geoErr || "Konum alınıyor..."}
        </p>
      </header>
      <div className="px-4 mt-4 space-y-4 max-w-xl mx-auto">
        <InstallAppButton testId="courier-install-btn" tone="light" />
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="aktif" className="flex-1" data-testid="courier-tab-active">Teslimatlarım ({orders.active.length})</TabsTrigger>
            <TabsTrigger value="teslim" className="flex-1" data-testid="courier-tab-delivered">Bugün Teslim ({orders.delivered_today.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "aktif" && list.map((o) => <OrderCard key={o.id} o={o} api={api} me={pos} onDone={load} />)}
        {tab === "teslim" && list.map((o) => (
          <div key={o.id} className="rounded-2xl bg-white border p-4 flex justify-between text-sm"><span>#{o.no} {o.customer_name}</span><span className="font-semibold">{fmt(o.total)} • {PAYMENTS[o.payment_method]}</span></div>
        ))}
        {!list.length && <div className="rounded-3xl border border-dashed p-10 text-center text-slate-500" data-testid="courier-empty">Şu an sipariş yok. Yeni sipariş geldiğinde sesli uyarı alacaksınız.</div>}
      </div>
    </div>
  );
}

export default function CourierApp() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const logout = useCallback(() => { localStorage.removeItem(KEY); setToken(null); }, []);
  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
